import { useCallback, useEffect, useRef, useState } from 'react';
import { universeApi } from '../lib/api';
import { sessionKey } from '../lib/auth';
import { sortAssets } from '../lib/sort';
import { UniverseApiError, type UniverseAsset, type UniverseSort } from '../lib/types';
import { useSession } from './useAuth';

export type FeedSource =
  | { kind: 'browse'; sort: Exclude<UniverseSort, 'relevant'> }
  | { kind: 'search'; q: string; sort: UniverseSort }
  | { kind: 'collection'; slug: string; sort: Exclude<UniverseSort, 'relevant'> };

const PAGE_SIZE = 36;
const SEARCH_LIMIT = 60;

export interface FeedState {
  items: UniverseAsset[];
  total: number | null;
  /** First load for this source has not resolved yet (items may be stale). */
  pending: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: { status: number } | null;
  /** The last next-page request failed; hasMore stays true so it can be retried. */
  moreFailed: boolean;
  /** Pages fetched for the current source and sort (1 after the first page). */
  pages: number;
  loadMore: () => void;
  retry: () => void;
}

/** The sort is part of the key: changing it discards the cursor and refetches page one. */
function sourceKey(source: FeedSource): string {
  switch (source.kind) {
    case 'browse':
      return `browse:${source.sort}`;
    case 'search':
      return `search:${source.sort}:${source.q}`;
    case 'collection':
      return `collection:${source.sort}:${source.slug}`;
  }
}

function fetchPage(source: FeedSource, cursor: string | null, signal?: AbortSignal) {
  if (source.kind === 'browse') {
    return universeApi.listAssets({ sort: source.sort, cursor, limit: PAGE_SIZE }, signal);
  }
  if (source.kind === 'collection') {
    return universeApi.getCollection({ slug: source.slug, sort: source.sort, cursor, limit: PAGE_SIZE }, signal);
  }
  throw new Error('search is not paginated');
}

export function useAssetFeed(source: FeedSource): FeedState {
  const viewer = sessionKey(useSession());
  const key = `${sourceKey(source)}|${viewer}`;
  const [rawItems, setRawItems] = useState<UniverseAsset[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pending, setPending] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<{ status: number } | null>(null);
  const [moreFailed, setMoreFailed] = useState(false);
  const [pages, setPages] = useState(0);
  const [attempt, setAttempt] = useState(0);

  const sourceRef = useRef(source);
  sourceRef.current = source;
  const loadingMoreRef = useRef(false);
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    const controller = new AbortController();
    const current = sourceRef.current;
    setPending(true);
    setError(null);
    setMoreFailed(false);
    setCursor(null);
    setHasMore(false);

    const request =
      current.kind === 'search'
        ? universeApi
            .search({ q: current.q, sort: current.sort, limit: SEARCH_LIMIT }, controller.signal)
            .then((r) => ({
              // Holding the complete result set, the client can apply the documented
              // comparator itself; a truncated set keeps the server's full-set order.
              items: r.items.length >= r.total ? sortAssets(r.items, current.sort) : r.items,
              next_cursor: null,
              has_more: false,
              total: r.total,
            }))
        : fetchPage(current, null, controller.signal).then((p) => ({ ...p, total: p.total ?? null }));

    request
      .then((page) => {
        setRawItems(page.items);
        setCursor(page.next_cursor);
        setHasMore(page.has_more);
        setTotal(page.total);
        setPages(1);
        setPending(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const status = typeof err === 'object' && err && 'status' in err ? Number(err.status) : 500;
        setRawItems([]);
        setHasMore(false);
        setTotal(null);
        setPages(0);
        setError({ status });
        setPending(false);
      });

    return () => controller.abort();
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const loadMore = useCallback(() => {
    const current = sourceRef.current;
    if (!hasMore || !cursor || pending || loadingMoreRef.current || current.kind === 'search') return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    const requestedFor = keyRef.current;
    fetchPage(current, cursor)
      .then((page) => {
        // A sort or source change while this page was in flight makes it stale.
        if (keyRef.current !== requestedFor) return;
        setRawItems((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          return [...prev, ...page.items.filter((a) => !seen.has(a.id))];
        });
        setCursor(page.next_cursor);
        setHasMore(page.has_more);
        setPages((n) => n + 1);
      })
      .catch((err: unknown) => {
        if (keyRef.current !== requestedFor) return;
        // The server refuses cursors from a list that has since changed; start over from page one.
        if (err instanceof UniverseApiError && err.status === 400) setAttempt((n) => n + 1);
        else setMoreFailed(true);
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [cursor, hasMore, pending]);

  return { items: rawItems, total, pending, loadingMore, hasMore, error, moreFailed, pages, loadMore, retry };
}
